/**
 * @voltxampere/web — `<vxa-layout>` (Sprint 11 → 15).
 *
 * Three-column desktop grid + Sprint 15 bottom waveform pane:
 *
 *   ┌───────────┬─────────────┬──────────────┐
 *   │  sidebar  │   canvas    │   inspector  │
 *   ├───────────┴─────────────┴──────────────┤
 *   │            <vxa-waveform>              │
 *   └────────────────────────────────────────┘
 *
 * The waveform pane is fixed at 260 px when expanded and 24 px
 * when collapsed (Sprint 15 Karar 1). F4 toggles the expanded
 * flag from any focus context (canvas-catalog).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";

import "./vxa-sidebar.js";
import "./vxa-canvas.js";
import "./vxa-inspector.js";
import "./vxa-waveform.js";
import "./vxa-bode-plot.js";

import { SignalController } from "../lit-utils/signal-controller.js";
import { waveformExpanded } from "./vxa-waveform.js";
import { waveformPaneVisible } from "../store/waveform-visibility.js";
import { currentACResult } from "../store/ac-runner.js";

@customElement("vxa-layout")
export class VxaLayout extends LitElement {
  // Lit reactive controller — Preact signal subscription drives
  // re-render on toggle. Field is intentionally read-only at the
  // statement level; the constructor side-effect (controller
  // registration) is the contract.
  private readonly _expanded = new SignalController(this, waveformExpanded);
  private readonly _waveVisible = new SignalController(this, waveformPaneVisible);
  // Sprint 21 C8 — D-20-11 closure. Bode plot mounts as a floating
  // overlay anchored to the bottom-right when an AC sweep result
  // is available. Manual mount stays viable for ad-hoc smoke; this
  // signal-driven mount picks up DEVRELER preset → simulator flows
  // automatically.
  private readonly _acResult = new SignalController(this, currentACResult);

  static override styles = css`
    :host {
      display: grid;
      grid-template-columns: var(--sidebar-width) 1fr var(--inspector-width);
      grid-template-rows: 1fr auto;
      grid-template-areas:
        "sidebar canvas inspector"
        "waveform waveform waveform";
      height: 100%;
      min-height: 0;
      background: var(--void);
    }

    .pane {
      min-height: 0;
      overflow: hidden;
    }
    .pane.sidebar {
      grid-area: sidebar;
      border-right: 1px solid var(--ivory-wisp);
      background: var(--ink);
    }
    .pane.canvas {
      grid-area: canvas;
    }
    .pane.inspector {
      grid-area: inspector;
      border-left: 1px solid var(--ivory-wisp);
      background: var(--ink);
    }
    .pane.waveform {
      grid-area: waveform;
      border-top: 1px solid var(--ivory-wisp);
      background: var(--ink);
      overflow: hidden;
    }
    .pane.waveform.expanded {
      height: 260px;
    }
    .pane.waveform.collapsed {
      height: 24px;
    }

    /* Sprint 21 C8 — Bode plot floating overlay. Visible only when
       currentACResult.value is non-null. Anchored to the canvas
       pane's bottom-right so it doesn't displace the existing
       three-column layout (D-20-11 closure for Sprint 21). */
    .bode-overlay {
      position: absolute;
      right: 16px;
      bottom: 16px;
      width: 480px;
      height: 280px;
      z-index: 50;
      box-shadow: 0 6px 28px rgba(0, 0, 0, 0.45);
      pointer-events: auto;
    }
    .pane.canvas {
      position: relative;
    }

    @media (max-width: 767px) {
      :host {
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr auto auto;
        grid-template-areas:
          "sidebar"
          "canvas"
          "inspector"
          "waveform";
      }
      .pane.sidebar {
        border-right: none;
        border-bottom: 1px solid var(--ivory-wisp);
        max-height: 40vh;
        overflow: auto;
      }
      .pane.inspector {
        border-left: none;
        border-top: 1px solid var(--ivory-wisp);
        max-height: 30vh;
        overflow: auto;
      }
    }
  `;

  override render() {
    const expanded = waveformExpanded.value;
    const waveClass = expanded ? "pane waveform expanded" : "pane waveform collapsed";
    const showBode = currentACResult.value !== null;
    // Sprint 80.Y L-A-2 — when waveformPaneVisible is false, the
    // entire waveform row collapses (no grid item declares
    // grid-area: waveform). Topbar toggle + Cmd+Shift+O + palette
    // command bring the pane back.
    const waveVisible = waveformPaneVisible.value;
    return html`
      <div class="pane sidebar"><vxa-sidebar></vxa-sidebar></div>
      <div class="pane canvas">
        <vxa-canvas></vxa-canvas>
        ${showBode
          ? html`<vxa-bode-plot class="bode-overlay" data-testid="bode-overlay"></vxa-bode-plot>`
          : null}
      </div>
      <div class="pane inspector"><vxa-inspector></vxa-inspector></div>
      ${waveVisible ? html`<div class=${waveClass}><vxa-waveform></vxa-waveform></div>` : null}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-layout": VxaLayout;
  }
}
